import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { RenderPanel } from '@/components/RenderPanel/RenderPanel';
import { TemplateEditor, type TemplateDraft } from '@/components/TemplateEditor/TemplateEditor';
import { TemplateFilterBar } from '@/components/TemplateFilterBar/TemplateFilterBar';
import {
  EMPTY_FILTERS,
  selectedCategory,
  type TemplateFilters,
} from '@/components/TemplateFilterBar/TemplateFilterBar.types';
import { TemplateGrid } from '@/components/TemplateGrid/TemplateGrid';
import { TemplateModal, type TemplateAction } from '@/components/TemplateModal/TemplateModal';
import {
  CATEGORIZE_MESSAGE_TEMPLATE,
  CREATE_MESSAGE_TEMPLATE,
  DELETE_MESSAGE_TEMPLATE,
  DUPLICATE_MESSAGE_TEMPLATE,
  EDIT_MESSAGE_TEMPLATE,
  MANAGED_STATUSES,
  MESSAGE_TEMPLATE_DETAIL,
  MY_MESSAGE_TEMPLATES,
  RENDER_MESSAGE_TEMPLATE,
  REORDER_MESSAGE_TEMPLATES,
  REVERT_MESSAGE_TEMPLATE,
  SET_MESSAGE_TEMPLATE_STATUS,
  TENANT_MESSAGE_TEMPLATES,
  type CategorizeMessageTemplateResult,
  type CreateMessageTemplateResult,
  type DeleteMessageTemplateResult,
  type Diagnostic,
  type DuplicateMessageTemplateResult,
  type EditMessageTemplateResult,
  type MessageTemplate,
  type MessageTemplateDetailResult,
  type MessageTemplateListVariables,
  type MessageTemplateScope,
  type MessageTemplateStatus,
  type MyMessageTemplatesResult,
  type RenderMessageTemplateResult,
  type RenderMessageTemplateVariables,
  type ReorderMessageTemplatesResult,
  type ReorderMessageTemplatesVariables,
  type RevertMessageTemplateResult,
  type SetMessageTemplateStatusResult,
  type TemplateCategory,
  type TemplateEnvelope,
  type TemplatePayload,
  type TenantMessageTemplatesResult,
} from '@/graphql/message-templates';
import { moveWithin, REORDER_SCOPE, sortForList, type TemplateList } from '@/lib/template-order';
import type { VariableBag } from '@/lib/variable-bag';
import { useToast } from '@/toast/useToast';

import styles from './TemplatesContainer.module.css';

/**
 * The message-templates harness: one filterable listing of every template the
 * token can see, and a modal over the one you picked.
 *
 * **Keystone does not resolve variables.** It holds no applicants and no
 * cases, so a template is stored, versioned and parsed here but filled in by
 * whoever is rendering it — app-web passes the bag it already has on screen.
 * That is why the modal carries a JSON textarea: it is standing in for that
 * caller, and typing a bag by hand is the only way to exercise the diagnostics
 * a wrong one produces.
 *
 * **Two list queries, one listing.** Keystone deliberately answers with two
 * lists and no precedence rule between them, which is a decision it leaves to
 * the client; this client merges them into one listing and puts a chip on each
 * row saying which scope it came from. They stay separate *queries* because
 * `Query.tenant` refuses a token that resolves no organisation — a Turnstile
 * login resolves one only when the person holds exactly one tenant membership
 * — and one document would take the personal list down with the brokerage one.
 */
export function TemplatesContainer() {
  const toast = useToast();
  const client = useApolloClient();

  const [filters, setFilters] = useState<TemplateFilters>(EMPTY_FILTERS);
  const [openId, setOpenId] = useState<string | null>(null);
  const [editing, setEditing] = useState<'create' | 'edit' | null>(null);
  const [busy, setBusy] = useState<'create' | 'edit' | TemplateAction | null>(null);
  const [revertingVersion, setRevertingVersion] = useState<number | null>(null);
  const [editorEnvelope, setEditorEnvelope] = useState<Envelope>(EMPTY_ENVELOPE);
  const [detailEnvelope, setDetailEnvelope] = useState<Envelope>(EMPTY_ENVELOPE);
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [reorderingList, setReorderingList] = useState<TemplateList | null>(null);
  const [reorderError, setReorderError] = useState<string | null>(null);

  const [render, setRender] = useState<TemplateEnvelope<string> | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  // Both lists are read with `MANAGED_STATUSES`, never with the status filter:
  // the filter narrows on the client like the other three, and the *whole*
  // live list is what a reorder has to name. Driving the query from the filter
  // would make a permutation Keystone refuses the moment anyone picked one.
  const listVariables: MessageTemplateListVariables = { statuses: MANAGED_STATUSES };
  const mine = useQuery<MyMessageTemplatesResult, MessageTemplateListVariables>(
    MY_MESSAGE_TEMPLATES,
    { variables: listVariables },
  );
  const brokerage = useQuery<TenantMessageTemplatesResult, MessageTemplateListVariables>(
    TENANT_MESSAGE_TEMPLATES,
    { variables: listVariables },
  );
  const detail = useQuery<MessageTemplateDetailResult, { id: string }>(MESSAGE_TEMPLATE_DETAIL, {
    variables: { id: openId ?? '' },
    skip: openId === null,
  });

  const [createTemplate] = useMutation<CreateMessageTemplateResult>(CREATE_MESSAGE_TEMPLATE);
  const [editTemplate] = useMutation<EditMessageTemplateResult>(EDIT_MESSAGE_TEMPLATE);
  const [duplicateTemplate] = useMutation<DuplicateMessageTemplateResult>(
    DUPLICATE_MESSAGE_TEMPLATE,
  );
  const [revertTemplate] = useMutation<RevertMessageTemplateResult>(REVERT_MESSAGE_TEMPLATE);
  const [categorizeTemplate] = useMutation<CategorizeMessageTemplateResult>(
    CATEGORIZE_MESSAGE_TEMPLATE,
  );
  const [setTemplateStatus] = useMutation<SetMessageTemplateStatusResult>(
    SET_MESSAGE_TEMPLATE_STATUS,
  );
  const [reorderTemplates] = useMutation<
    ReorderMessageTemplatesResult,
    ReorderMessageTemplatesVariables
  >(REORDER_MESSAGE_TEMPLATES);
  const [deleteTemplate] = useMutation<DeleteMessageTemplateResult>(DELETE_MESSAGE_TEMPLATE);

  // A brokerage-facing scope needs an organisation on the request, and that is
  // exactly what having reached `tenant` proves. Offering the other two
  // regardless would put a `MissingOrgError` behind a control that looks
  // available. Both `PERSON_IN_TENANT` and `TENANT` need one — the first names
  // a brokerage the token has to be in — so they narrow together, and a
  // duplicate's destination is the same question about the same token as a
  // create's scope.
  const hasBrokerage = Boolean(brokerage.data?.tenant?.id);
  const writableScopes: readonly MessageTemplateScope[] = hasBrokerage
    ? ['PERSON', 'PERSON_IN_TENANT', 'TENANT']
    : ['PERSON'];

  // Each list in Keystone's own order — `(position, createdAt, id)` — rather
  // than in the order the array arrived in. They are the same on a fresh read
  // and diverge the moment a reorder lands: the mutation answers with every
  // row's new `position`, which Apollo normalises onto the cached rows, while
  // the *array* the list query returned still holds the old order and nothing
  // replaces it. Sorting here is what lets a reorder show without a refetch.
  const lists = useMemo(
    () => ({
      mine: sortForList(mine.data?.me.messageTemplates ?? []),
      brokerage: sortForList(brokerage.data?.tenant.messageTemplates ?? []),
    }),
    [brokerage.data, mine.data],
  );

  const visible = useMemo(
    () => ({
      mine: filters.scope === 'brokerage' ? [] : lists.mine.filter(matches(filters)),
      brokerage: filters.scope === 'mine' ? [] : lists.brokerage.filter(matches(filters)),
    }),
    [filters, lists],
  );

  const templates = useMemo(
    () => [...visible.mine, ...visible.brokerage],
    [visible.brokerage, visible.mine],
  );

  // What a reorder may name: every *live* row, which is not every row on
  // screen. A soft-deleted template stays in the listing until the next load —
  // its `deletedAt` is what confirms the delete — but Keystone's live set no
  // longer holds it, so naming it would have the whole permutation refused.
  const live = useCallback(
    (rows: readonly MessageTemplate[]) =>
      rows.filter((row) => row.deletedAt === null && !deletedIds.has(row.id)),
    [deletedIds],
  );

  const listOfId = useCallback(
    (id: string): TemplateList | null => {
      if (lists.mine.some((row) => row.id === id)) {
        return 'mine';
      }
      return lists.brokerage.some((row) => row.id === id) ? 'brokerage' : null;
    },
    [lists],
  );

  const categories = useMemo(
    () =>
      mergeCategories(
        mine.data?.me.messageTemplateCategories ?? [],
        brokerage.data?.tenant.messageTemplateCategories ?? [],
      ),
    [brokerage.data, mine.data],
  );

  const open = useCallback((id: string | null) => {
    setOpenId(id);
    setEditing(null);
    setDetailEnvelope(EMPTY_ENVELOPE);
    setEditorEnvelope(EMPTY_ENVELOPE);
    // The last render belonged to the template that is no longer on screen.
    setRender(null);
    setRenderError(null);
  }, []);

  const refetchLists = useCallback(async () => {
    await Promise.all([mine.refetch(), hasBrokerage ? brokerage.refetch() : Promise.resolve()]);
  }, [brokerage, hasBrokerage, mine]);

  /**
   * Run one write and read its envelope.
   *
   * Two kinds of failure arrive by two routes and are kept apart all the way
   * to the screen. A bad *body* comes back in `errors` on a successful
   * response — that is the whole design of the envelope — while a refusal
   * about the *request* (no organisation for a brokerage scope, a version
   * number the template never had, a template that is not yours) is a
   * transport error and throws.
   */
  const run = useCallback(
    async (
      action: 'create' | 'edit' | TemplateAction,
      into: (envelope: Envelope) => void,
      call: () => Promise<TemplatePayload | undefined>,
    ): Promise<TemplatePayload | null> => {
      setBusy(action);
      into(EMPTY_ENVELOPE);
      try {
        const payload = await call();
        if (!payload) {
          into({ errors: [], error: NO_PAYLOAD });
          return null;
        }
        into({ errors: payload.errors, error: null });
        return payload;
      } catch (cause) {
        into({ errors: [], error: messageOf(cause) });
        return null;
      } finally {
        setBusy(null);
      }
    },
    [],
  );

  const onCreate = async (draft: TemplateDraft) => {
    const payload = await run('create', setEditorEnvelope, async () => {
      const { data } = await createTemplate({
        variables: {
          scope: draft.scope,
          type: 'FREE_FORM',
          title: draft.title,
          body: draft.body,
          category: draft.category.trim() === '' ? null : draft.category,
          status: draft.status,
        },
      });
      return data?.createMessageTemplate;
    });
    if (payload?.result) {
      toast.success({ title: 'Template created', description: draft.title });
      await refetchLists();
      open(payload.result.id);
    }
  };

  const onEdit = async (draft: TemplateDraft) => {
    if (!openId) {
      return;
    }
    const payload = await run('edit', setEditorEnvelope, async () => {
      const { data } = await editTemplate({
        variables: { id: openId, title: draft.title, body: draft.body },
      });
      return data?.editMessageTemplate;
    });
    // The payload carries the whole template, versions included, so the cache
    // takes the new version without a refetch — the listing row and the
    // history are the same normalised row.
    if (payload?.result) {
      setEditing(null);
      toast.success({ title: 'New version saved' });
    }
  };

  const onDuplicate = async (destination: MessageTemplateScope) => {
    if (!openId) {
      return;
    }
    const payload = await run('duplicate', setDetailEnvelope, async () => {
      const { data } = await duplicateTemplate({ variables: { id: openId, destination } });
      return data?.duplicateMessageTemplate;
    });
    // A fork is a second template, so no cached list contains it yet.
    if (payload?.result) {
      toast.success({ title: 'Template duplicated' });
      await refetchLists();
      open(payload.result.id);
    }
  };

  const onRevert = async (versionNumber: number) => {
    if (!openId) {
      return;
    }
    setRevertingVersion(versionNumber);
    try {
      const payload = await run('revert', setDetailEnvelope, async () => {
        const { data } = await revertTemplate({ variables: { id: openId, versionNumber } });
        return data?.revertMessageTemplate;
      });
      if (payload?.result) {
        toast.success({ title: `Restored version ${versionNumber}` });
      }
    } finally {
      setRevertingVersion(null);
    }
  };

  const onCategorize = async (category: string | null) => {
    if (!openId) {
      return;
    }
    const payload = await run('categorize', setDetailEnvelope, async () => {
      const { data } = await categorizeTemplate({ variables: { id: openId, category } });
      return data?.categorizeMessageTemplate;
    });
    // Filing changes which group a template is in, and the category filter is
    // built from Keystone's enumeration of exactly that — so the counts beside
    // the options go stale unless the lists are asked again.
    if (payload?.result) {
      toast.success({ title: category ? `Filed under “${category}”` : 'Category cleared' });
      await refetchLists();
    }
  };

  const onSetStatus = async (status: MessageTemplateStatus) => {
    if (!openId) {
      return;
    }
    // No refetch. The payload carries the whole template, so the cached row —
    // the same normalised row the listing draws — takes the new status on its
    // own; and both lists are read with every status, so nothing enters or
    // leaves them. The category counts are read the same way and do not move
    // either, which is why filing refetches and this does not.
    const payload = await run('status', setDetailEnvelope, async () => {
      const { data } = await setTemplateStatus({ variables: { id: openId, status } });
      return data?.setMessageTemplateStatus;
    });
    if (payload?.result) {
      toast.success({ title: status === 'DRAFT' ? 'Moved to drafts' : 'Template is live' });
    }
  };

  /**
   * Move one row one place in its own list.
   *
   * The step is against what is on screen and the permutation is against the
   * whole live list — see `moveWithin`. The two lists are reordered separately
   * because they are two lists to Keystone, with one order each; `PERSON` is
   * the scope for the personal one whichever of the two person scopes a row is
   * really in, since both name the same list.
   *
   * A refusal here is a transport error rather than a diagnostic: an
   * `orderedIds` that is not an exact permutation describes the *request*, and
   * nothing about a body. It is shown above the listing, which is what it is
   * about, rather than in the modal.
   */
  const onMove = async (id: string, direction: 'up' | 'down') => {
    const list = listOfId(id);
    if (!list) {
      return;
    }
    const orderedIds = moveWithin({
      all: live(lists[list]),
      visible: live(visible[list]),
      direction,
      id,
    });
    if (!orderedIds) {
      return;
    }
    setReorderingList(list);
    setReorderError(null);
    try {
      const { data } = await reorderTemplates({
        variables: { scope: REORDER_SCOPE[list], orderedIds },
      });
      if (!data) {
        setReorderError(NO_PAYLOAD);
      }
    } catch (cause) {
      setReorderError(messageOf(cause));
    } finally {
      setReorderingList(null);
    }
  };

  const canMove = useCallback(
    (id: string, direction: 'up' | 'down') => {
      const list = listOfId(id);
      if (!list) {
        return false;
      }
      const rows = live(visible[list]);
      const index = rows.findIndex((row) => row.id === id);
      if (index === -1) {
        return false;
      }
      return direction === 'up' ? index > 0 : index < rows.length - 1;
    },
    [listOfId, live, visible],
  );

  const onDelete = async () => {
    if (!openId) {
      return;
    }
    const deleting = openId;
    const payload = await run('delete', setDetailEnvelope, async () => {
      const { data } = await deleteTemplate({ variables: { id: deleting } });
      return data?.deleteMessageTemplate;
    });
    // The soft-deleted row stays on screen — its `deletedAt` is what confirms
    // the delete, where a row that vanished would leave nothing to read the
    // outcome off. The listing is deliberately not refetched for the same
    // reason the files harness does not; it is gone on the next load.
    if (payload?.result) {
      setDeletedIds((ids) => new Set(ids).add(deleting));
      toast.success({ title: 'Template deleted' });
    }
  };

  const onRender = async ({
    versionNumber,
    variables,
  }: {
    versionNumber: number | null;
    variables: VariableBag | null;
  }) => {
    if (!openId) {
      return;
    }
    setRendering(true);
    setRenderError(null);
    try {
      const { data } = await client.query<
        RenderMessageTemplateResult,
        RenderMessageTemplateVariables
      >({
        query: RENDER_MESSAGE_TEMPLATE,
        variables: { id: openId, versionNumber, variables },
        // A render is not a row and there is nothing to normalise it by, so a
        // cached answer would just be the last one — and rendering the same
        // template twice with two bags is the point of the panel.
        fetchPolicy: 'network-only',
      });
      if (!data) {
        setRender(null);
        setRenderError(NO_PAYLOAD);
        return;
      }
      setRender(data.renderMessageTemplate);
    } catch (cause) {
      setRender(null);
      setRenderError(messageOf(cause));
    } finally {
      setRendering(false);
    }
  };

  const opened = detail.data?.messageTemplate ?? null;
  const listErrors = [mine.error?.message, brokerage.error?.message].filter(
    (message): message is string => Boolean(message),
  );

  return (
    <section>
      <div className={styles.header}>
        <div>
          <h2 className={styles.heading}>Message templates</h2>
          <p className={styles.lede}>
            Keystone stores, versions and parses templates; it does not fill them in. Open one and
            render it against a variable bag you type — that is the call app-web makes with the
            applicant and case data already on its screen.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditorEnvelope(EMPTY_ENVELOPE);
            setEditing('create');
          }}
          type="button"
        >
          New template
        </Button>
      </div>

      {!hasBrokerage && !brokerage.loading ? (
        <Callout title="No brokerage on this login">
          Turnstile mints no tenant claim, so Keystone resolves your brokerage from its own
          membership rows — and only when you hold exactly one. Without it, <code>tenant</code> is
          refused and the two brokerage-facing scopes cannot be written.
        </Callout>
      ) : null}

      {editing === 'create' ? (
        <div className={styles.creator}>
          <TemplateEditor
            busy={busy === 'create'}
            error={editorEnvelope.error}
            errors={editorEnvelope.errors}
            key="create"
            mode="create"
            onCancel={() => setEditing(null)}
            onSubmit={onCreate}
            writableScopes={writableScopes}
          />
        </div>
      ) : null}

      <TemplateFilterBar
        categories={categories}
        filters={filters}
        onChange={setFilters}
        onReset={() => setFilters(EMPTY_FILTERS)}
      />

      {reorderError ? (
        <Callout tone="danger" title="Keystone refused the reorder">
          {reorderError}
        </Callout>
      ) : null}

      <TemplateGrid
        canMove={canMove}
        deletedIds={deletedIds}
        errors={listErrors}
        loading={mine.loading || brokerage.loading}
        onMove={onMove}
        onOpen={open}
        reorderingList={reorderingList}
        templates={templates}
      />

      {openId ? (
        <TemplateModal
          busyAction={busy === 'create' || busy === 'edit' ? null : busy}
          duplicateScopes={writableScopes}
          error={detail.error ? detail.error.message : null}
          key={`modal-${openId}`}
          loading={detail.loading}
          onCategorize={onCategorize}
          onClose={() => open(null)}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onEdit={() => {
            setEditorEnvelope(EMPTY_ENVELOPE);
            setEditing('edit');
          }}
          onRevert={onRevert}
          onSetStatus={onSetStatus}
          revertingVersion={revertingVersion}
          template={opened}
          writeError={detailEnvelope.error}
          writeErrors={detailEnvelope.errors}
        >
          {editing === 'edit' && opened ? (
            <TemplateEditor
              busy={busy === 'edit'}
              error={editorEnvelope.error}
              errors={editorEnvelope.errors}
              initial={{
                title: opened.currentVersion?.title ?? '',
                body: opened.currentVersion?.body ?? '',
              }}
              key={`edit-${openId}-${opened.currentVersionId}`}
              mode="edit"
              onCancel={() => setEditing(null)}
              onSubmit={onEdit}
              writableScopes={writableScopes}
            />
          ) : null}

          {opened ? (
            <RenderPanel
              busy={rendering}
              currentVersionId={opened.currentVersionId}
              error={renderError}
              key={`render-${opened.id}`}
              onRender={onRender}
              render={render}
              versions={opened.versions}
            />
          ) : null}
        </TemplateModal>
      ) : null}
    </section>
  );
}

/**
 * Whether one row survives the filters, scope aside — that one is applied by
 * choosing which lists to read from at all.
 *
 * Category, status and title are narrowed here rather than at Keystone even
 * though `messageTemplates(categories:, statuses:)` takes all three, because
 * the listing is already both lists in memory: refetching one of them per
 * keystroke would spend a round trip to hide rows that are already on the
 * client. Status has a second reason on top of that one — the reorder needs
 * the whole live list, drafts included, whatever the filter says.
 *
 * A row with a null `status` reads as `ACTIVE`, which is Keystone's own
 * reading of the NULL its backfill left: a template matched by nothing would
 * vanish from the list with nothing on the row to explain it.
 */
function matches(filters: TemplateFilters) {
  const search = filters.search.trim().toLowerCase();
  const named = selectedCategory(filters.category);

  return (template: MessageTemplate): boolean => {
    if (filters.category === 'ungrouped' && template.category !== null) {
      return false;
    }
    if (named !== null && template.category !== named) {
      return false;
    }
    if (filters.status !== 'any' && (template.status ?? 'ACTIVE') !== filters.status) {
      return false;
    }
    if (search && !(template.currentVersion?.title ?? '').toLowerCase().includes(search)) {
      return false;
    }
    return true;
  };
}

/**
 * Keystone enumerates a category per scope, so a group both lists use is two
 * rows describing one option. Summed, because the listing they filter is the
 * two lists merged — and the counts have to describe what the filter will
 * actually leave on screen.
 */
function mergeCategories(
  mine: readonly TemplateCategory[],
  brokerage: readonly TemplateCategory[],
): TemplateCategory[] {
  const totals = new Map<string | null, number>();
  for (const row of [...mine, ...brokerage]) {
    totals.set(row.category, (totals.get(row.category) ?? 0) + row.templateCount);
  }
  return [...totals].map(([category, templateCount]) => ({ category, templateCount }));
}

interface Envelope {
  errors: readonly Diagnostic[];
  error: string | null;
}

const EMPTY_ENVELOPE: Envelope = { errors: [], error: null };

/** A 200 with no `data` at all: not a diagnostic, and not an exception either. */
const NO_PAYLOAD = 'Keystone answered with no payload.';

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
